import React, { useEffect, useMemo, useState } from 'react';
import authService from '../../services/authService';
import Toast from '../Toast';

const DOC_OPTIONS = [
  { value: 'FAQQandAs', label: 'FAQQandAs' },
  { value: 'DisclaimerSections', label: 'DisclaimerSections' },
  { value: 'TermsSections', label: 'TermsSections' },
  { value: 'PrivacySections', label: 'PrivacySections' },
  { value: 'AppInfoDocuments', label: 'Documents (Learn/AppInfo)' }
];

function normalizeDocTypeForApi(docType) {
  const s = (docType || '').toLowerCase();
  if (s === 'faqqandas') return 'faqqandas';
  if (s === 'termssections') return 'termssections';
  if (s === 'privacysections') return 'privacysections';
  if (s === 'disclaimersections') return 'disclaimersections';
  if (s === 'appinfodocuments') return 'appinfodocuments';
  return s;
}

const AdminDocsEdit = () => {
  const [docType, setDocType] = useState('');
  const [ids, setIds] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [fieldA, setFieldA] = useState('');
  const [fieldB, setFieldB] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const labels = useMemo(() => {
    const isFaq = (docType || '').toLowerCase() === 'faqqandas';
    return isFaq
      ? { a: 'Question', b: 'Answer' }
      : { a: 'Title', b: 'Content' };
  }, [docType]);

  const resetForm = () => {
    setIds([]);
    setSelectedId('');
    setFieldA('');
    setFieldB('');
  };

  useEffect(() => {
    if (!docType) {
      resetForm();
      return;
    }
    (async () => {
      try {
        setLoading(true);
        setSelectedId('');
        setFieldA('');
        setFieldB('');
        const t = authService.getToken();
        const dt = normalizeDocTypeForApi(docType);
        if (dt === 'appinfodocuments') {
          const res = await fetch('/api/docs?type=AppInfo', { headers: { Authorization: `Bearer ${t}` } });
          if (!res.ok) throw new Error('Failed to load docs');
          const data = await res.json();
          const items = Array.isArray(data.items) ? data.items : [];
          const norm = items.map(it => ({ id: it.id ?? it.Id, slug: it.slug ?? it.Slug, title: it.title ?? it.Title }));
          setIds(norm);
        } else {
          const res = await fetch(`/api/admin/docs/list?docType=${encodeURIComponent(dt)}`, { headers: { Authorization: `Bearer ${t}` } });
          if (!res.ok) throw new Error('Failed to load IDs');
          const data = await res.json();
          setIds(Array.isArray(data.items) ? data.items : []);
        }
      } catch (e) {
        setIds([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [docType]);

  const onSelectId = async (id) => {
    setSelectedId(id);
    setFieldA('');
    setFieldB('');
    if (!id) return;
    try {
      setLoading(true);
      const t = authService.getToken();
      const dt = normalizeDocTypeForApi(docType);
      if (dt === 'appinfodocuments') {
        const found = (ids || []).find(x => String(x.id) === String(id));
        const slug = found?.slug;
        if (!slug) throw new Error('Invalid selection');
        const res = await fetch(`/api/docs/${encodeURIComponent(slug)}`, { headers: { Authorization: `Bearer ${t}` } });
        if (!res.ok) throw new Error('Failed to load document');
        const data = await res.json();
        const doc = data?.doc || data;
        setFieldA(doc?.title || '');
        setFieldB(doc?.content || '');
      } else {
        const res = await fetch(`/api/admin/docs/item?docType=${encodeURIComponent(dt)}&id=${encodeURIComponent(id)}`, { headers: { Authorization: `Bearer ${t}` } });
        if (!res.ok) throw new Error('Failed to load item');
        const item = await res.json();
        const isFaq = (docType || '').toLowerCase() === 'faqqandas';
        setFieldA(isFaq ? (item.question || '') : (item.title || ''));
        setFieldB(isFaq ? (item.answer || '') : (item.content || ''));
      }
    } catch {
      // noop
    } finally {
      setLoading(false);
    }
  };

  const validateForSql = () => {
    const isFaq = (docType || '').toLowerCase() === 'faqqandas';
    const a = (fieldA || '').trim();
    const b = (fieldB || '').trim();
    if (isFaq) {
      if (!a || a.length > 500) return false;
      if (!b) return false;
    } else {
      if (!a || a.length > 200) return false;
      if (!b) return false;
    }
    return true;
  };

  const onSave = async () => {
    if (!docType || !selectedId) return;
    if (!validateForSql()) {
      setToast({ show: true, message: 'Syntax Error', type: 'warning' });
      return;
    }
    try {
      setLoading(true);
      const t = authService.getToken();
      const dt = normalizeDocTypeForApi(docType);
      if (dt === 'appinfodocuments') {
        const found = (ids || []).find(x => String(x.id) === String(selectedId));
        const slug = found?.slug;
        if (!slug) throw new Error('Invalid selection');
        const putBody = {
          slug,
          title: (fieldA || '').trim(),
          content: (fieldB || '').trim(),
          type: 'AppInfo',
          isPublished: true
        };
        const res = await fetch(`/api/docs/${encodeURIComponent(selectedId)}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(putBody)
        });
        if (!res.ok) throw new Error('Save failed');
      } else {
        const isFaq = (docType || '').toLowerCase() === 'faqqandas';
        const body = {
          docType: dt,
          id: Number(selectedId),
          question: isFaq ? (fieldA || '').trim() : undefined,
          answer: isFaq ? (fieldB || '').trim() : undefined,
          title: !isFaq ? (fieldA || '').trim() : undefined,
          content: !isFaq ? (fieldB || '').trim() : undefined
        };
        const res = await fetch('/api/admin/docs/item/update', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error('Save failed');
      }
      setFieldA('');
      setFieldB('');
      setToast({ show: true, message: 'Text saved', type: 'success' });
    } catch {
      setToast({ show: true, message: 'Syntax Error', type: 'warning' });
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    setDocType('');
    resetForm();
  };

  return (
    <div className="admin-section">
      <h2 className="admin-header">Docs Edit</h2>
      <div className="form-vertical" style={{ gap: 10 }}>
        <label style={{ fontWeight: 600, fontSize:14, color:'#374151' }}>Select a document to edit</label>
        <select 
          value={docType} 
          onChange={e => setDocType(e.target.value)} 
          style={{
            maxWidth: 340,
            padding: '10px 12px',
            border: '1.5px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            background: '#fff',
            cursor: 'pointer',
            width: '100%',
            boxSizing: 'border-box'
          }}
          onFocus={(e)=>{
            e.target.style.borderColor = '#667eea';
            e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
          }}
          onBlur={(e)=>{
            e.target.style.borderColor = '#e5e7eb';
            e.target.style.boxShadow = 'none';
          }}
        >
          <option value="">-- Select --</option>
          {DOC_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        {docType && (
          <>
            <label style={{ marginTop: 8, fontWeight: 600, fontSize:14, color:'#374151' }}>[Id]</label>
            <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
              <select
                value={selectedId}
                onChange={e => onSelectId(e.target.value)}
                style={{
                  minWidth: '80px',
                  padding: '10px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  background: '#fff',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                }}
                onFocus={(e)=>{
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e)=>{
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="">--</option>
                {ids.map(x => (
                  <option key={x.id} value={x.id}>{x.id}</option>
                ))}
              </select>

              <div style={{ display: 'flex', gap: 10, flex: 1 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize:14, color:'#374151', marginBottom:4 }}>{labels.a}</label>
                  <textarea
                    value={fieldA}
                    onChange={e => setFieldA(e.target.value)}
                    style={{
                      width: '100%',
                      height: 160,
                      resize: 'vertical',
                      overflowY: 'auto',
                      padding: '10px 12px',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      background: '#fff',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e)=>{
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e)=>{
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                <div style={{ flex: 2 }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize:14, color:'#374151', marginBottom:4 }}>{labels.b}</label>
                  <textarea
                    value={fieldB}
                    onChange={e => setFieldB(e.target.value)}
                    style={{
                      width: '100%',
                      height: 160,
                      resize: 'vertical',
                      overflowY: 'auto',
                      padding: '10px 12px',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      background: '#fff',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e)=>{
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e)=>{
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="admin-controls" style={{ gap: 8, marginTop: 10, display:'flex', justifyContent:'flex-end' }}>
              <button 
                onClick={onCancel} 
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  background: '#fff',
                  color: '#6b7280',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: loading ? 0.6 : 1
                }}
                onMouseEnter={(e)=>{
                  if (!loading) {
                    e.target.style.background = '#f3f4f6';
                    e.target.style.borderColor = '#d1d5db';
                  }
                }}
                onMouseLeave={(e)=>{
                  if (!loading) {
                    e.target.style.background = '#fff';
                    e.target.style.borderColor = '#e5e7eb';
                  }
                }}
              >
                Cancel
              </button>
              <button 
                onClick={onSave} 
                disabled={loading || !selectedId}
                style={{
                  padding: '10px 20px',
                  background: (loading || !selectedId) ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: (loading || !selectedId) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: (loading || !selectedId) ? 'none' : '0 2px 4px rgba(102, 126, 234, 0.2)',
                  opacity: (loading || !selectedId) ? 0.7 : 1
                }}
                onMouseEnter={(e)=>{
                  if (!loading && selectedId) {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                  }
                }}
                onMouseLeave={(e)=>{
                  if (!loading && selectedId) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.2)';
                  }
                }}
              >
                Save
              </button>
            </div>
          </>
        )}
      </div>

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: '', type: 'success' })}
      />
    </div>
  );
};

export default AdminDocsEdit;


